import React, { useState, useEffect } from "react";
import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Container,
    Divider,
    Grid,
    TextField,
    Typography,
    Stack,
    Paper,
    Snackbar,
    Alert,
    AlertColor
} from "@mui/material";
import SaveIcon from '@mui/icons-material/Save';
import { Breadcrumb } from 'components';
import { profileGet, profileUpdate } from "services/profile";

interface SnackbarState {
    open: boolean;
    message: string;
    type: AlertColor;
}

const ManageProfile = () => {
    const [form, setForm] = useState({
        id: '',
        firstName: '',
        lastName: '',
        email: '',
        puid: '',
        designation: ''
    });
    const [error, setError] = useState<SnackbarState>({
        open: false,
        type: 'error',
        message: ''
    });
    const [designationError, setDesignationError] = useState(false);

    const getProfileInfo = () => {
        profileGet().then((res: any) => {
            if (res.status === 200) {
                const data = res.data;
                setForm({
                    id: data.id,
                    firstName: data.firstname,
                    lastName: data.lastname,
                    email: data.email,
                    puid: data.puid,
                    designation: data.designation
                });
            } else {
                setError({
                    open: true,
                    type: 'error',
                    message: res.data || 'Failed to fetch profile information.'
                });
            }
        }).catch(() => {
            setError({
                open: true,
                type: 'error',
                message: 'An unexpected error occurred while fetching profile information.'
            });
        });
    }

    const handleSave = () => {
        if (!form.designation.trim()) {
            setDesignationError(true);
            return;
        }

        setDesignationError(false);

        profileUpdate({ ...form }).then((res: any) => {
            if (res.status === 200) {
                getProfileInfo();
                setError({
                    open: true,
                    type: 'success',
                    message: 'Designation updated successfully.'
                });
            } else {
                setError({
                    open: true,
                    type: 'error',
                    message: res.data || 'Failed to update profile information.'
                });
            }
        }).catch(() => {
            setError({
                open: true,
                type: 'error',
                message: 'An unexpected error occurred while updating profile information.'
            });
        });
    }

    useEffect(() => {
        getProfileInfo();
    }, []);

    const getInitials = (first, last) => {
        return `${first?.charAt(0) || ""}${last?.charAt(0) || ""}`.toUpperCase();
    };

    let nav: any = [{ name: 'Home', path: '/home' }, { name: 'Manage Profile', path: '/home/manage-profile' }];

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 0', cursor: 'pointer' }}>
                <Breadcrumb navList={nav} />
            </div>
            <Container maxWidth="lg" sx={{ py: 4 }}>

                {/* GRID LAYOUT */}
                <Grid container spacing={3}>

                    {/* LEFT PROFILE PANEL */}
                    <Grid item xs={12} md={4}>
                        <Paper
                            sx={{
                                p: 3,
                                borderRadius: 4,
                                position: "sticky",
                                top: 20,
                                background: "#003883",
                                color: "#fff",
                            }}
                        >
                            <Stack alignItems="center" spacing={2}>
                                <Avatar
                                    sx={{
                                        width: 90,
                                        height: 90,
                                        bgcolor: "#fff",
                                        color: "#1e3c72",
                                        fontWeight: 700,
                                        fontSize: 28,
                                    }}
                                >
                                    {getInitials(form.firstName, form.lastName)}
                                </Avatar>

                                <Typography variant="h6" fontWeight={700} sx={{ color: '#fff' }}>
                                    {form.firstName} {form.lastName}
                                </Typography>

                                <Typography sx={{ fontSize: 12, color: '#fff' }}>
                                    {form.email}
                                </Typography>

                                <Divider sx={{ width: "100%", borderColor: "rgba(255,255,255,0.2)" }} />

                                <Box textAlign="center">
                                    <Typography variant="caption" sx={{ color: '#fff' }}>Designation</Typography>
                                    <Typography fontWeight={600} sx={{ color: '#fff' }}>
                                        {form.designation}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Paper>
                    </Grid>

                    {/* RIGHT FORM PANEL */}
                    <Grid item xs={12} md={8}>
                        <Paper sx={{ p: 3, borderRadius: 4, border: '1px solid #ddd' }}>

                            <Typography variant="h5" fontWeight={700} mb={3}>
                                Manage Profile
                            </Typography>

                            <Grid container spacing={2}>

                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="First Name"
                                        value={form.firstName}
                                        disabled
                                    />
                                </Grid>

                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Last Name"
                                        value={form.lastName}
                                        disabled
                                    />
                                </Grid>

                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Email"
                                        value={form.email}
                                        disabled
                                    />
                                </Grid>

                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="PUID"
                                        value={form.puid}
                                        disabled
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Designation"
                                        value={form.designation}
                                        required
                                        error={designationError}
                                        helperText={designationError ? "Designation is required" : ""}
                                        onChange={(e) => {
                                            setForm({ ...form, designation: e.target.value });
                                            if (e.target.value.trim()) {
                                                setDesignationError(false);
                                            }
                                        }}
                                    />
                                </Grid>

                            </Grid>

                            {/* SAVE BUTTON */}
                            <Box mt={3} textAlign="right">
                                <Button
                                    variant="contained"
                                    onClick={handleSave}
                                    startIcon={<SaveIcon />}
                                >
                                    Save Changes
                                </Button>
                            </Box>

                        </Paper>
                    </Grid>

                </Grid>

                {/* SNACKBAR */}
                <Snackbar
                    open={error.open}
                    autoHideDuration={3000}
                    onClose={() => setError({ ...error, open: false })}
                >
                    <Alert onClose={() => setError({ ...error, open: false })} severity={error.type}>
                        {error.message}
                    </Alert>
                </Snackbar>

            </Container>
        </>
    );
};

export default ManageProfile;